import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseMarketCurrentGradeDistributionGrid {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private selectedGroupBy: string = 'firm';
    private selectedColorBy: string = 'action';
    private selectedTimeWindow: string = '1month';
    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
      headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Current Grade Distribution</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item">
                        <select id="groupBySelect" title="Group by">
                            <option value="firm">Analyst Firm</option>
                            <option value="sector">Sector</option>
                            <option value="industry">Industry</option>
                            <option value="market">Entire Market</option>
                        </select>
                    </div>
                    <span></span>
                    <div class="item">
                        <select id="timeWindowSelect" title="Select time window">
                            <option value="1year">1 Year</option>
                            <option value="6month">6 Month</option>
                            <option value="3month">3 Month</option>
                            <option value="1month" selected>1 Month</option>
                            <option value="1week">1 Week</option>
                        </select>
                    </div>
                </div>

                <div class="chartDv currentGradeDistributionMain" id="currentGradeDistributionContainer"></div>

            </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
        // attach filter select change handlers so switching filters reloads data
        const groupEl = this.shadowRoot.querySelector('#groupBySelect') as HTMLSelectElement | null;
        const colorEl = this.shadowRoot.querySelector('#colorBySelect') as HTMLSelectElement | null;
        const timeEl = this.shadowRoot.querySelector('#timeWindowSelect') as HTMLSelectElement | null;

        if (groupEl) {
            groupEl.value = this.selectedGroupBy;
            groupEl.addEventListener('change', () => {
                try {
                    this.selectedGroupBy = groupEl.value;
                    this.setupChart(this.config.symbol!);
                } catch (e) { console.error(e); }
            });
        }
        if (colorEl) {
            colorEl.value = this.selectedColorBy;
            colorEl.addEventListener('change', () => {
                try {
                    this.selectedColorBy = colorEl.value;
                    this.updateDisplay();
                } catch (e) { console.error(e); }
            });
        }
        if (timeEl) {
            timeEl.value = this.selectedTimeWindow;
            timeEl.addEventListener('change', () => {
                try {
                    this.selectedTimeWindow = timeEl.value;
                    this.setupChart(this.config.symbol!);
                } catch (e) { console.error(e); }
            });
        }
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }


    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        try {
            const params: string[] = [];
            params.push(`p_group_level=${encodeURIComponent(this.selectedGroupBy)}`);
            params.push(`p_time_period=${encodeURIComponent(this.selectedTimeWindow)}`);
            // optional group key (e.g., specific firm or symbol) if provided via config
            if ((this.config as any).groupKey) {
                params.push(`p_group_key=${encodeURIComponent((this.config as any).groupKey)}`);
            }
            const url = `${APIURL}/v1/discover/current-grade-distribution?${params.join('&')}`;

            const res = await apiGet({
                method: 'GET',
                url,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            this.currentData = json.data || [];

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }


    private updateDisplay(): void {
        if (!this.currentData) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.renderSentimentChart();
    }


    private renderSentimentChart(): void {
        const container = this.shadowRoot.querySelector('#currentGradeDistributionContainer');
        if (!container) return;

        this.drawSentimentChart();
    }

    private drawSentimentChart(): void {
        const container = this.shadowRoot.querySelector('#currentGradeDistributionContainer') as HTMLDivElement;
if (!container) return;

if (!this.currentData || this.currentData.length === 0) {
    container.removeAttribute('data-testing-id');
    container.innerHTML = getNoDataHTML();
    return;
}
    container.setAttribute('data-testing-id', 'current-grade-distribution-chart');


        if (!this.currentData || this.currentData.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const grouped: Record<string, { upgrade: number; downgrade: number; hold: number; total: number }> = {};
        this.currentData.forEach((d: any) => {
            const key = d.group_key || d.groupKey || d.group || 'Unknown';
            if (!grouped[key]) grouped[key] = { upgrade: 0, downgrade: 0, hold: 0, total: 0 };
            grouped[key].upgrade += Number(d.upgrade || 0);
            grouped[key].downgrade += Number(d.downgrade || 0);
            grouped[key].hold += Number(d.hold || 0);
            grouped[key].total += Number(d.total != null ? d.total : ((d.upgrade || 0) + (d.downgrade || 0) + (d.hold || 0)));
        });

        const firms = Object.keys(grouped);
        // build rows
        const rows: string[] = [];
        firms.forEach((f) => {
            const g = grouped[f];
            const total = g.total || 1;
            const upW = Math.round((g.upgrade / total) * 100);
            const downW = Math.round((g.downgrade / total) * 100);
            const holdW = Math.max(0, 100 - upW - downW);

            const rowHtml = `
                <div class="item">
                    <div class="bodyRow rowItm">
                        <p>${this.escapeHtml(f)}</p>
                        <p>${g.total}</p>
                        <p class="upgradeTxt">${g.upgrade}</p>
                        <p class="downgradeTxt">${g.downgrade}</p>
                        <p class="holdTxt">${g.hold}</p>
                    </div>
                    <div class="pillDv" role="presentation">
                        <div class="pillInner">
                            <div class="segment upgrade" style="flex:${g.upgrade};"></div>
                            <div class="segment downgrade" style="flex:${g.downgrade};"></div>
                            <div class="segment hold" style="flex:${g.hold};"></div>
                        </div>
                    </div>
                </div>
            `;
            rows.push(rowHtml);
        });

        const headerHTML = `
        <div class="headerDv">
            <div class="legendRow">
                <p><span class="upgrade"></span> Upgrade</p>
                <p><span class="downgrade"></span> Downgrade</p>
                <p><span class="hold"></span> Hold</p>
            </div>
            <div class="headerRow rowItm">
                <p>Firm Name</p>
                <p>Total</p>
                <p>Upgrade</p>
                <p>Downgrade</p>
                <p>Hold</p>
            </div>
        </div>`;

        container.innerHTML = headerHTML + '<div class="bodyDv">' + rows.join('') + '</div>';
    }

    private escapeHtml(str: string): string {
        if (!str) return '';
        return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }



    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketCurrentGradeDistributionGrid: typeof TraderverseMarketCurrentGradeDistributionGrid; } }
if (typeof window !== 'undefined') { window.TraderverseMarketCurrentGradeDistributionGrid = TraderverseMarketCurrentGradeDistributionGrid; }
